import { PowerPlantData, POWER_PLANT_SPECS } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  plant: PowerPlantData;
  isOwner: boolean;
  onMaintenance: () => void;
  onToggle: () => void;
}

export default function PlantStatus({ plant, isOwner, onMaintenance, onToggle }: Props) {
  const { isDark } = useTheme();
  const spec = POWER_PLANT_SPECS[plant.type];

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const statusColors = {
    running: { bg: "bg-emerald-400/15", text: "text-emerald-400", dot: "bg-emerald-400", label: "稼働中" },
    maintenance: { bg: "bg-amber-400/15", text: "text-amber-400", dot: "bg-amber-400", label: "メンテナンス中" },
    offline: { bg: "bg-red-400/15", text: "text-red-400", dot: "bg-red-400", label: "停止中" },
  };
  const sc = statusColors[plant.operatingStatus];

  const outputPercent = Math.round((plant.currentOutput / plant.capacity) * 100);
  const contractedMW = plant.powerContracts
    .filter((c) => c.status === "active")
    .reduce((sum, c) => sum + c.contractedMW, 0);
  const availableMW = plant.capacity - contractedMW;
  const monthlyRevenue = plant.powerContracts
    .filter((c) => c.status === "active")
    .reduce((sum, c) => sum + c.contractedMW * c.pricePerMWh * 24 * 30, 0);

  const daysSinceMaintenance = Math.floor(
    (Date.now() - new Date(plant.lastMaintenance).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className={`rounded-2xl border p-6 mb-6 ${cardBg}`}>
      <div className="flex flex-col lg:flex-row items-start gap-6">
        {/* Plant Icon */}
        <div className={`w-20 h-20 flex items-center justify-center rounded-2xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
          <i className={`${spec.icon} text-4xl ${spec.color}`}></i>
        </div>

        {/* Plant Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className={`text-2xl font-black ${textPrimary}`}>{plant.name}</h1>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${sc.bg} ${sc.text}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${sc.dot} animate-pulse`}></span>
              {sc.label}
            </span>
            <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              {spec.label}
            </span>
          </div>
          <p className={`text-sm ${textSecondary} mb-4`}>
            設立: {new Date(plant.builtAt).toLocaleDateString("ja-JP")} •
            最終メンテナンス: {daysSinceMaintenance}日前
          </p>

          {/* Output Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className={textSecondary}>現在の発電量</span>
              <span className={`font-bold ${textPrimary}`}>{plant.currentOutput} MW / {plant.capacity} MW ({outputPercent}%)</span>
            </div>
            <div className={`h-3 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
              <div
                className={`h-3 rounded-full transition-all ${outputPercent >= 80 ? "bg-gradient-to-r from-emerald-400 to-emerald-500" : outputPercent >= 50 ? "bg-gradient-to-r from-amber-400 to-amber-500" : "bg-gradient-to-r from-red-400 to-red-500"}`}
                style={{ width: `${outputPercent}%` }}
              ></div>
            </div>
          </div>

          {/* Efficiency Bar */}
          <div className="space-y-2 mt-3">
            <div className="flex items-center justify-between text-xs">
              <span className={textSecondary}>効率</span>
              <span className={`font-bold ${plant.efficiency >= 70 ? "text-emerald-400" : plant.efficiency >= 40 ? "text-amber-400" : "text-red-400"}`}>
                {plant.efficiency}%
              </span>
            </div>
            <div className={`h-2 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
              <div
                className={`h-2 rounded-full transition-all ${plant.efficiency >= 70 ? "bg-emerald-400" : plant.efficiency >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                style={{ width: `${plant.efficiency}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3 flex-shrink-0 w-full lg:w-auto">
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>設備容量</p>
            <p className={`font-black text-lg whitespace-nowrap ${textPrimary}`}>{plant.capacity}</p>
            <p className={`text-xs ${textSecondary}`}>MW</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>月次収益</p>
            <p className="text-emerald-400 font-black text-lg whitespace-nowrap">{(monthlyRevenue / 1000).toFixed(0)}K</p>
            <p className={`text-xs ${textSecondary}`}>coins</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>契約済MW</p>
            <p className={`font-black text-lg whitespace-nowrap ${textPrimary}`}>{contractedMW}</p>
            <p className={`text-xs ${textSecondary}`}>/ {plant.capacity} MW</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>CO₂排出</p>
            <p className={`font-black text-lg whitespace-nowrap ${plant.co2Emission < 0.1 ? "text-emerald-400" : plant.co2Emission < 0.5 ? "text-amber-400" : "text-red-400"}`}>
              {plant.co2Emission}
            </p>
            <p className={`text-xs ${textSecondary}`}>t/MWh</p>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className={`mt-4 pt-4 border-t ${divider} grid grid-cols-2 sm:grid-cols-4 gap-4`}>
        <div>
          <p className={`text-xs ${textSecondary} mb-0.5`}>累計発電量</p>
          <p className={`font-bold ${textPrimary}`}>{plant.totalGenerated.toLocaleString()} MWh</p>
        </div>
        <div>
          <p className={`text-xs ${textSecondary} mb-0.5`}>燃料残量</p>
          <p className={`font-bold ${plant.fuelLevel > 50 ? "text-emerald-400" : plant.fuelLevel > 20 ? "text-amber-400" : "text-red-400"}`}>
            {plant.fuelLevel}%
          </p>
        </div>
        <div>
          <p className={`text-xs ${textSecondary} mb-0.5`}>空き容量</p>
          <p className={`font-bold ${availableMW > 0 ? "text-emerald-400" : "text-red-400"}`}>{availableMW} MW</p>
        </div>
        <div>
          <p className={`text-xs ${textSecondary} mb-0.5`}>月次メンテ費</p>
          <p className="text-red-400 font-bold">{plant.maintenanceCost.toLocaleString()} coins</p>
        </div>
      </div>

      {/* Actions */}
      {isOwner && (
        <div className={`mt-4 pt-4 border-t ${divider} flex flex-wrap gap-3`}>
          <button
            onClick={onMaintenance}
            className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap ${
              daysSinceMaintenance > 30
                ? "bg-amber-400/20 text-amber-400 border border-amber-400/30 hover:bg-amber-400/30"
                : isDark ? "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10" : "bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <i className="ri-tools-line"></i>
            メンテナンス実施 ({plant.maintenanceCost.toLocaleString()} coins)
          </button>
          <button
            onClick={onToggle}
            className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap ${
              plant.operatingStatus === "running"
                ? "bg-red-400/10 text-red-400 border border-red-400/20 hover:bg-red-400/20"
                : "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 hover:bg-emerald-400/20"
            }`}
          >
            <i className={plant.operatingStatus === "running" ? "ri-pause-circle-line" : "ri-play-circle-line"}></i>
            {plant.operatingStatus === "running" ? "停止する" : "起動する"}
          </button>
        </div>
      )}
    </div>
  );
}
